import type { MatchState, TeamSide } from "@golazo/shared";
import { t } from "../i18n/index.js";
import { cameraModeLabel, cycleCameraMode } from "../render/cameraModes.js";
import {
  isFullscreen,
  isStandalone,
  onFullscreenChange,
  supportsFullscreen,
  toggleFullscreen,
} from "../mobile/fullscreen.js";
import "./matchChrome.css";

export type MatchOutcome = "win" | "lose" | "draw";

/** Rotulos de los dos botones del final. Por defecto, los del partido local. */
export interface ResultLabels {
  /** `null` esconde el boton: en la carrera no hay revancha que valga. */
  primary?: string | null;
  secondary?: string;
  /**
   * Tu lado. Sin el, "ganar" era que ganase el local: el visitante (online o
   * de carrera) leia ¡VICTORIA! con confeti despues de perder.
   */
  side?: TeamSide;
}

export interface MatchChrome {
  setExitHandler(handler: (() => void) | null): void;
  showExit(): void;
  hideExit(): void;
  /**
   * `mvpHtml` es un bloque ya renderizado (ver `mvpBlockHtml`). Se pasa hecho
   * para que esta capa no dependa del modelo de estadisticas.
   */
  /**
   * `panels.summary` es el resumen de siempre (MVP, logros, clip, tarjeta) y
   * `panels.stats` la tabla jugador a jugador. Se pasan los dos y esta capa
   * los alterna: asi `matchChrome` no necesita conocer el modelo de
   * estadisticas, que es lo que ya evitaba antes.
   */
  showResult(
    match: MatchState,
    panels?: { summary: string; stats: string },
    labels?: ResultLabels,
  ): Promise<"menu" | "rematch">;
  hideResult(): void;
  /** Hay resultados a la vista, esperando que se elija. */
  resultOpen(): boolean;
  /** Elegir por el jugador (un aviso de "Unirse" en los resultados). */
  dismissResult(choice: "menu" | "rematch"): void;
  dispose(): void;
}

/**
 * La frase del final. DOS campos, no cuatro.
 *
 * Habia tambien `kicker` ("transmision nacional") y `stamp` ("momento viral ·
 * aprobado"): cuatro trozos de texto apilados encima del marcador que, sumados
 * al titular a 7,2rem, se comian el 40% de la pantalla antes de que apareciera
 * un solo dato del partido. La gracia se conserva entera con uno; lo que
 * sobraba era la repeticion.
 *
 * Todo pasa por `t()`, incluidos los titulares. Media tabla estaba sin traducir
 * y en ingles salia una pantalla mitad y mitad: "ABSOLUTE GOLAZO!" con
 * "transmision nacional" encima.
 */
interface ResultCopy {
  headline: string;
  sub: string;
}

const WIN_COPY: readonly ResultCopy[] = [
  {
    headline: t("¡GOLAZO TOTAL!"),
    sub: t("El césped todavía vibra. Guarda esto: acabas de firmar un clip eterno."),
  },
  {
    headline: t("ESTO ES CINE"),
    sub: t("No jugaste un partido. Rodaste una película con balón."),
  },
  {
    headline: t("DOMINIO ABSOLUTO"),
    sub: t("El rival vino a pelear. Se fue a pedir autógrafos."),
  },
  {
    headline: t("NO HUBO DUELO"),
    sub: t("Hubo coronación. El marcador es solo la prueba forense."),
  },
];

const LOSE_COPY: readonly ResultCopy[] = [
  {
    headline: t("TE HAN BAILADO"),
    sub: t("Duele. Sirve. La revancha ya está escribiendo tu nombre."),
  },
  {
    headline: t("NOCHE NEGRA"),
    sub: t("Hoy el césped no te sonrió. Mañana le sacas los dientes."),
  },
  {
    headline: t("TE HAN COMIDO VIVO"),
    sub: t("Aplaude al rival… y luego cómetelo en la vuelta."),
  },
  {
    headline: t("EL RIVAL FIRMÓ"),
    sub: t("Tú pusiste el alma. Ellos pusieron el marcador. Empate emocional."),
  },
];

const DRAW_COPY: readonly ResultCopy[] = [
  {
    headline: t("NADIE SE RINDIÓ"),
    sub: t("El marcador empató. El orgullo, jamás."),
  },
  {
    headline: t("MISMA HAMBRE"),
    sub: t("Dos equipos. Cero concesiones. Un final que pide bis."),
  },
  {
    headline: t("TABLAS INCENDIARIAS"),
    sub: t("No hubo ganador. Hubo espectáculo. Eso también es victoria."),
  },
];

function pick<T>(list: readonly T[]): T {
  return list[Math.floor(Math.random() * list.length)]!;
}

function outcomeOf(match: MatchState, side: TeamSide = "home"): MatchOutcome {
  const mine = match.score[side];
  const theirs = match.score[side === "home" ? "away" : "home"];
  if (mine > theirs) return "win";
  if (mine < theirs) return "lose";
  return "draw";
}

function copyFor(outcome: MatchOutcome): ResultCopy {
  if (outcome === "win") return pick(WIN_COPY);
  if (outcome === "lose") return pick(LOSE_COPY);
  return pick(DRAW_COPY);
}

function spawnConfetti(layer: HTMLElement, outcome: MatchOutcome): void {
  layer.replaceChildren();
  if (outcome === "lose") return;

  const colors =
    outcome === "win"
      ? ["#ffe082", "#e5484d", "#f2fff5", "#ff8a8e"]
      : ["#f2fff5", "#8fa8ff", "#ffe082", "#3e63dd"];

  for (let i = 0; i < 42; i++) {
    const bit = document.createElement("i");
    bit.style.left = `${Math.random() * 100}%`;
    bit.style.background = colors[i % colors.length]!;
    bit.style.animationDuration = `${1.8 + Math.random() * 2.4}s`;
    bit.style.animationDelay = `${Math.random() * 0.6}s`;
    bit.style.transform = `rotate(${Math.random() * 360}deg)`;
    layer.appendChild(bit);
  }
}

export function createMatchChrome(root: HTMLElement = document.body): MatchChrome {
  const exitBtn = document.createElement("button");
  exitBtn.id = "match-exit";
  exitBtn.type = "button";
  exitBtn.textContent = t("Menú");
  exitBtn.classList.add("is-hidden");
  exitBtn.title = t("Volver al menú (Esc)");

  /*
   * Pantalla completa: al lado del boton de Menu, y solo si sirve de algo.
   *
   * No se enseña si el navegador no lo implementa (Safari de iPhone) ni si ya
   * se esta jugando sin navegador alrededor (PWA instalada, "Añadir a pantalla
   * de inicio"): en los dos casos seria un boton que no cambia nada, y un boton
   * muerto en mitad de un partido es peor que no tenerlo.
   *
   * Va aqui y no en un menu de ajustes porque es una decision que se toma
   * jugando —"esta barra me tapa el campo"— y hay que poder deshacerla igual de
   * rapido.
   */
  const fullscreenBtn = document.createElement("button");
  fullscreenBtn.id = "match-fullscreen";
  fullscreenBtn.type = "button";
  fullscreenBtn.classList.add("is-hidden");
  const fullscreenUseful = supportsFullscreen() && !isStandalone();

  const syncFullscreenBtn = (): void => {
    const on = isFullscreen();
    fullscreenBtn.textContent = on ? "⤡" : "⛶";
    fullscreenBtn.title = on ? t("Salir de pantalla completa") : t("Pantalla completa");
    fullscreenBtn.setAttribute("aria-label", fullscreenBtn.title);
    fullscreenBtn.setAttribute("aria-pressed", on ? "true" : "false");
  };
  syncFullscreenBtn();
  const stopFullscreenWatch = onFullscreenChange(syncFullscreenBtn);
  fullscreenBtn.addEventListener("click", () => {
    void toggleFullscreen();
  });

  /*
   * Pantalla completa DESDE EL PRINCIPIO.
   *
   * Antes solo aparecia con el partido ya empezado, y llegaba tarde por dos
   * razones: la barra de direcciones del movil se come el alto justo cuando
   * eliges equipo y modo, y el bloqueo de horizontal solo se puede pedir DENTRO
   * de pantalla completa — o sea que quien no entraba antes de jugar se
   * encontraba la reja de "gira el movil" en mitad del menu.
   *
   * `hideExit()` ya no lo esconde: el boton de MENU es del partido, este es de
   * la ventana, y son cosas distintas aunque compartan esquina.
   */
  if (fullscreenUseful) fullscreenBtn.classList.remove("is-hidden");

  /*
   * Modos de camara: cuanto campo quieres ver.
   *
   * Cicla AMPLIA -> NORMAL -> CERCA en un toque, sin abrir nada. Es una
   * decision que se toma jugando ("no veo a donde chuto" / "no distingo a los
   * mios"), asi que enterrarla en un menu de ajustes seria pedirle a alguien
   * que salga del partido para arreglar el partido.
   */
  const cameraBtn = document.createElement("button");
  cameraBtn.id = "match-camera";
  cameraBtn.type = "button";
  cameraBtn.classList.add("is-hidden");

  const syncCameraBtn = (): void => {
    cameraBtn.textContent = cameraModeLabel();
    const label = t("Cámara: {mode}", { mode: cameraModeLabel() });
    cameraBtn.title = label;
    cameraBtn.setAttribute("aria-label", label);
  };
  syncCameraBtn();
  cameraBtn.addEventListener("click", () => {
    cycleCameraMode();
    syncCameraBtn();
  });

  const result = document.createElement("div");
  result.id = "match-result";
  result.classList.add("is-hidden");
  result.innerHTML = `
    <div class="result-burst" aria-hidden="true"></div>
    <div class="result-confetti" data-confetti aria-hidden="true"></div>
    <div class="result-shell">
      <!--
        CABECERA EN UNA FRANJA, no en una columna.

        Antes eran cinco elementos apilados y centrados —antetitulo, titular a
        7,2rem, frase, marcador a 5,5rem y sello— que juntos ocupaban el 40% de
        la pantalla. Lo que el jugador viene a mirar (lo que ha hecho, lo que se
        lleva) empezaba por debajo del pliegue y habia que buscarlo.

        Ahora el veredicto, el marcador y los botones comparten UNA fila: se lee
        de un vistazo, ocupa un tercio de lo que ocupaba, y todo lo demas sube.
        Los botones siguen arriba por la misma razon de siempre — lo que se
        quiere hacer al acabar un partido es volver a jugar.
      -->
      <header class="result-head">
        <div class="result-verdict">
          <h2 class="result-headline" data-headline></h2>
          <p class="result-sub" data-sub></p>
        </div>
        <div class="result-score" aria-live="polite">
          <span class="home" data-home>0</span>
          <span class="sep">-</span>
          <span class="away" data-away>0</span>
        </div>
        <div class="result-actions">
          <button type="button" class="result-primary" data-rematch>${t("Revancha")}</button>
          <button type="button" class="result-secondary" data-menu>${t("Menú principal")}</button>
        </div>
      </header>
      <!--
        Dos vistas del mismo final: el resumen (lo que ha pasado) y la tabla
        jugador a jugador (lo que ha hecho cada uno). Antes solo existia la
        primera, asi que al acabar el partido todo tu trabajo desaparecia.
      -->
      <div class="result-tabs" role="tablist">
        <button type="button" class="result-tab is-active" role="tab" data-panel="summary">
          ${t("RESUMEN")}
        </button>
        <button type="button" class="result-tab" role="tab" data-panel="stats">
          ${t("ESTADÍSTICAS")}
        </button>
      </div>
      <div class="result-mvp" data-mvp></div>
    </div>
  `;

  root.appendChild(exitBtn);
  root.appendChild(fullscreenBtn);
  root.appendChild(cameraBtn);
  root.appendChild(result);

  const confetti = result.querySelector("[data-confetti]") as HTMLElement;
  const headline = result.querySelector("[data-headline]") as HTMLElement;
  const sub = result.querySelector("[data-sub]") as HTMLElement;
  const homeScore = result.querySelector("[data-home]") as HTMLElement;
  const awayScore = result.querySelector("[data-away]") as HTMLElement;
  const mvpSlot = result.querySelector("[data-mvp]") as HTMLElement | null;

  /**
   * Contenido de las dos pestañas. Se guarda en vez de pintarse una sola vez
   * porque el resumen lleva dentro el reproductor del clip y la tarjeta de
   * resultado: volver a inyectarlo al cambiar de pestaña los reconstruiria y
   * el clip empezaria de cero cada vez.
   */
  let panelHtml = { summary: "", stats: "" };

  const showPanel = (which: "summary" | "stats"): void => {
    if (mvpSlot) mvpSlot.innerHTML = which === "stats" ? panelHtml.stats : panelHtml.summary;
    for (const tab of result.querySelectorAll<HTMLButtonElement>(".result-tab")) {
      tab.classList.toggle("is-active", tab.dataset.panel === which);
      tab.setAttribute("aria-selected", tab.dataset.panel === which ? "true" : "false");
    }
  };

  for (const tab of result.querySelectorAll<HTMLButtonElement>(".result-tab")) {
    tab.addEventListener("click", () => {
      showPanel(tab.dataset.panel === "stats" ? "stats" : "summary");
    });
  }
  const rematchBtn = result.querySelector("[data-rematch]") as HTMLButtonElement;
  const menuBtn = result.querySelector("[data-menu]") as HTMLButtonElement;

  let onExit: (() => void) | null = null;
  let resolveResult: ((choice: "menu" | "rematch") => void) | null = null;
  let resultOpen = false;

  const hideResult = (): void => {
    result.classList.add("is-hidden");
    confetti.replaceChildren();
    resolveResult = null;
    resultOpen = false;
  };

  const finish = (choice: "menu" | "rematch"): void => {
    if (!resolveResult) return;
    const done = resolveResult;
    hideResult();
    done(choice);
  };

  exitBtn.addEventListener("click", () => onExit?.());
  rematchBtn.addEventListener("click", () => finish("rematch"));
  menuBtn.addEventListener("click", () => finish("menu"));

  return {
    setExitHandler(handler) {
      onExit = handler;
    },

    showExit() {
      if (resultOpen) return;
      exitBtn.classList.remove("is-hidden");
      // Al empezar cada partido: con dos jugadores la camara viene impuesta
      // (campo entero) y la etiqueta decia la de antes.
      syncCameraBtn();
      cameraBtn.classList.remove("is-hidden");
      if (fullscreenUseful) fullscreenBtn.classList.remove("is-hidden");
    },

    hideExit() {
      exitBtn.classList.add("is-hidden");
      // La camara es del partido; la pantalla completa es de la ventana y se
      // queda puesta tambien en los menus.
      cameraBtn.classList.add("is-hidden");
    },

    showResult(match: MatchState, panels?: { summary: string; stats: string }, labels?: ResultLabels) {
      // En online los dos botones son otros: la sala sigue viva, asi que lo
      // natural es volver a ella, y "menu" es salir de la sala.
      rematchBtn.textContent = labels?.primary ?? t("Revancha");
      rematchBtn.hidden = labels?.primary === null;
      menuBtn.textContent = labels?.secondary ?? t("Menú principal");
      const outcome = outcomeOf(match, labels?.side);
      const copy = copyFor(outcome);
      result.dataset.outcome = outcome;
      headline.textContent = copy.headline;
      sub.textContent = copy.sub;
      homeScore.textContent = String(match.score.home);
      awayScore.textContent = String(match.score.away);
      panelHtml = panels ?? { summary: "", stats: "" };
      showPanel("summary");
      spawnConfetti(confetti, outcome);
      resultOpen = true;
      result.classList.remove("is-hidden");
      exitBtn.classList.add("is-hidden");
      fullscreenBtn.classList.add("is-hidden");
      /*
       * `preventScroll` no es opcional aqui.
       *
       * `#match-result` es `overflow: hidden`, pero eso no impide que el
       * navegador lo desplace para traer a la vista lo que acaba de recibir el
       * foco — y lo hacia: 120 px de desplazamiento que se comian el titular y
       * el marcador, sin barra de desplazamiento que lo delatara ni forma de
       * volver arriba. El boton esta en la primera franja, asi que no hay nada
       * que traer a la vista; lo unico que hacia ese `focus()` era esconder la
       * cabecera.
       */
      (rematchBtn.hidden ? menuBtn : rematchBtn).focus({ preventScroll: true });

      return new Promise((resolve) => {
        resolveResult = resolve;
      });
    },

    hideResult,
    resultOpen: () => resolveResult !== null,
    dismissResult: (choice) => finish(choice),

    dispose() {
      onExit = null;
      resolveResult = null;
      stopFullscreenWatch();
      exitBtn.remove();
      fullscreenBtn.remove();
      result.remove();
    },
  };
}
